import React, { useContext, useEffect, useState } from 'react'
import SeatGrid from './SeatGrid'
import { Box, Button, Paper, Stack, Typography, Snackbar, Alert, useTheme } from '@mui/material'
import { ReservationContext } from '../../context/ReservationContext'
import { fetchSeats, reserveSeats } from '../backend'
import { displaySeatIds } from '../../utils/display'
import { isBooked, hasSeatGaps } from '../../utils/util'
import { LoadingSkeleton } from '../LoadingSkeleton'
import PropTypes from 'prop-types'

export function SeatSelection({ setPreviousStepCompleted, handleNext }) {
  const { selectedSeats, setSelectedSeats, showId, scheduleId, totalTickets, sessionUUID, setSessionUUID, setExpiresAt, enforceSeatGaps } =
    useContext(ReservationContext)
  const [loading, setLoading] = useState(true)
  const [initialSeats, setInitialSeats] = useState([])
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('error')
  const [unavailableSeats, setUnavailableSeats] = useState([])
  const [hasGaps, setHasGaps] = useState(false)
  const theme = useTheme()

  useEffect(() => {
    try {
      fetchSeats(showId, scheduleId, sessionUUID).then((res) => {
        setInitialSeats(res)
      })
    } catch (err) {
      setSnackbarMessage('Erreur réseau lors du chargement des sièges; veuillez réessayer plus tard')
      console.error('Erreur lors du chargement des sièges:', err)
    } finally {
      setLoading(false)
    }
  }, [setInitialSeats, setLoading, showId, scheduleId, sessionUUID, setSelectedSeats])
  const selectedCount = selectedSeats.length

  const toggleSeat = async (seat) => {
    if (isBooked(seat, sessionUUID, selectedSeats)) {
      return
    }

    const exists = selectedSeats.some((s) => s.id === seat.id)
    let newSelectedSeats

    if (exists) {
      // retire le siège
      newSelectedSeats = selectedSeats.filter((s) => s.id !== seat.id)
      setInitialSeats((prev) => {
        return prev.map((s) => (s.id === seat.id ? { ...s, status: 'available' } : s))
      })
      // TODO adapt initial list
    } else if (selectedSeats.length < totalTickets) {
      // ajoute le siège si pas complet
      newSelectedSeats = [...selectedSeats, seat]
    } else {
      return
    }
    setSelectedSeats(newSelectedSeats)
    if (newSelectedSeats.length === totalTickets && enforceSeatGaps) {
      setHasGaps(hasSeatGaps(newSelectedSeats, initialSeats))
    } else {
      setHasGaps(false)
    }
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setOpenSnackbar(false)
  }

  const confirmSelection = async () => {
    if (hasGaps) return
    // Réserver les nouveaux sièges sélectionnés
    try {
      const response = await reserveSeats(
        selectedSeats.map((s) => s.id),
        showId,
        scheduleId,
        sessionUUID
      )
      setSessionUUID(response.sessionUUID)
      if (response.expiresAt) setExpiresAt(response.expiresAt)
      setPreviousStepCompleted(true)
      handleNext()
    } catch (error) {
      // Afficher l'erreur via snackbar
      setSnackbarMessage(error?.body?.message ? error.body.message : error.message ? error.message : 'Erreur réseau lors de la réservation')
      setSnackbarSeverity('error')
      if (error?.body?.seats) {
        setUnavailableSeats(error.body.seats)
      }
      setOpenSnackbar(true)

      // Re-fetch les sièges pour mettre à jour l'état
      const updatedSeats = await fetchSeats(showId, scheduleId, sessionUUID)
      // Reset panier
      setSelectedSeats([])
      setHasGaps(false)
      setInitialSeats(updatedSeats)
      setOpenSnackbar(true)
    }
  }

  return (
    <Paper
      elevation={3}
      sx={{
        minHeight: '400px',
        maxHeight: { xs: '50vh', sm: '60vh' },
        overflowY: 'auto',
        p: { xs: 1, sm: 2 },
        borderRadius: 10
      }}
    >
      <Typography variant='h5' sx={{ mb: 1, fontWeight: 'bold', color: 'title.main' }}>
        3. Sélection des places
      </Typography>
      {loading ? (
        <LoadingSkeleton text={' Chargement des places disponibles...'} />
      ) : (
        <>
          <div style={stageStyle}>SCÈNE</div>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              alignItems: 'flex-start'
            }}
          >
            <Paper elevation={0} sx={{ bgcolor: 'background.default', borderRadius: 2, p: 1.5, minWidth: '200px', flexShrink: 0 }}>
              <Typography variant='body2' sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Légende
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.seat.available, borderRadius: 0.5 }} />
                <Typography variant='body2'>Place disponible (à sélectionner)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.seat.occupied, borderRadius: 0.5 }} />
                <Typography variant='body2'>Déjà réservé</Typography>
              </Box>
            </Paper>

            <SeatGrid initialSeats={initialSeats} selectedSeats={selectedSeats} onSeatClick={toggleSeat} sessionUUID={sessionUUID} />
          </Box>
          <Box
            sx={{
              position: { xs: 'static', sm: 'fixed' },
              right: { sm: '20px' },
              top: { sm: '60%' },
              transform: { sm: 'translateY(-50%)' },
              width: { xs: '100%', sm: '300px' },
              minHeight: { xs: 'auto', sm: '300px' },
              padding: '20px',
              backgroundColor: 'background.paper',
              border: { xs: 'none', sm: '2px dashed #1976d2' },
              borderRadius: '8px',
              boxShadow: 3,
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', marginBottom: '10px' }}>
              <Typography variant='h6' gutterBottom sx={{ color: '#1976d2' }}>
                Places sélectionnées ({selectedCount}/{totalTickets})
              </Typography>
              {selectedCount > 0 && <Typography variant='body2'>{displaySeatIds(selectedSeats)}</Typography>}
              {hasGaps && (
                <Typography variant='body2' sx={{ color: 'error.main', mt: 0.5 }}>
                  Evitez de laisser des places isolées par votre réservation.
                </Typography>
              )}
            </Box>
            <Stack spacing={1}>
              <Button
                variant='contained'
                color='primary'
                onClick={confirmSelection}
                disabled={selectedCount < totalTickets || hasGaps}
                fullWidth
              >
                Confirmer la sélection
              </Button>
              <Button variant='outlined' color='secondary' onClick={() => setSelectedSeats([])} disabled={selectedCount === 0} fullWidth>
                Annuler
              </Button>
            </Stack>
          </Box>
        </>
      )}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={10000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
          {unavailableSeats.length > 0 && <Box sx={{ mt: 1 }}>Places indisponibles: {unavailableSeats.join(', ')}</Box>}
        </Alert>
      </Snackbar>
    </Paper>
  )
}

const stageStyle = {
  margin: '20px auto',
  width: '60%',
  padding: '10px',
  background: '#333',
  color: 'white',
  borderRadius: '10px'
}

SeatSelection.propTypes = {
  setPreviousStepCompleted: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
}
